// Bonus (PRD §3.4, BP1–BP7; rev. 1.6, Chris 2026-10-10). Each person's compensation letter states an
// annual target per bonus type ("$25,000 profitability bonus, $15,000 sales bonus"); OTE is base plus
// those targets. An admin can override at any point, from a date onward, either one type's amount or
// the whole bonus, and the cost model uses the override. There are no pools: a type's total is the
// sum of its people's amounts.

import type { Iso } from "./dates";
import { live, type BonusOverrideRecord, type BonusTargetRecord, type Entered } from "./people";
import { splitExact } from "./money";

export interface BonusType {
  id: string;
  name: string;
  active: boolean;
  /** Counts in the OTE rate, so projects carry it (default on). */
  includedInOte: boolean;
  description?: string;
}

/** PRD §4.2's examples. Admins rename, add and retire types. */
export const SEEDED_BONUS_TYPES: readonly BonusType[] = [
  { id: "profitability", name: "Company profitability", active: true, includedInOte: true },
  { id: "retention", name: "Customer retention", active: true, includedInOte: true },
  { id: "sales", name: "Sales performance", active: true, includedInOte: true },
];

export interface BonusLine {
  typeId: string;
  name: string;
  includedInOte: boolean;
  /** The annual target from the letter in effect. */
  target: number;
  /** A per-type override in effect, if any. */
  override: number | null;
  /** What counts: the override if there is one, otherwise the target. */
  amount: number;
}

export interface BonusOn {
  lines: BonusLine[];
  targetTotal: number;
  /** A whole-bonus override in effect, if any. It replaces every line's amount. */
  totalOverride: number | null;
  /** The annual bonus the OTE rate carries on the day. */
  total: number;
  /** The letter in effect, if any. */
  letter?: BonusTargetRecord;
}

/** The latest live record starting on or before `day` and not ended before it; a later entry wins a tie. */
function latest<T extends Entered & { from: Iso; to?: Iso }>(records: readonly T[], day: Iso): T | undefined {
  let best: T | undefined;
  for (const r of records) {
    if (r.voided || r.from > day || (r.to && r.to < day)) continue;
    if (!best || r.from > best.from || (r.from === best.from && r.enteredAt > best.enteredAt)) best = r;
  }
  return best;
}

/** The overrides in effect on `day`: at most one per type, and at most one for the whole bonus. */
export function overridesOn(overrides: readonly BonusOverrideRecord[] = [], day: Iso): BonusOverrideRecord[] {
  return [...new Set(overrides.map((o) => o.typeId))].map((k) => latest(overrides.filter((o) => o.typeId === k), day)).filter((o): o is BonusOverrideRecord => !!o);
}

/**
 * The bonus in effect on `day`. Per-type overrides replace that type's target; a whole-bonus override
 * replaces the total. Only types included in OTE count toward the total; a whole-bonus override is,
 * by definition, the bonus the OTE rate carries.
 */
export function bonusOn(targets: readonly BonusTargetRecord[] = [], overrides: readonly BonusOverrideRecord[] = [], types: readonly BonusType[], day: Iso): BonusOn {
  const letter = latest(targets, day);
  const name = new Map(types.map((t) => [t.id, t]));
  const typeIds = new Set([...(letter?.targets.map((t) => t.typeId) ?? []), ...live(overrides).filter((o) => o.typeId).map((o) => o.typeId!)]);
  const lines: BonusLine[] = [];
  for (const typeId of typeIds) {
    const target = letter?.targets.find((t) => t.typeId === typeId)?.amount ?? 0;
    const o = latest(overrides.filter((x) => x.typeId === typeId), day);
    if (!target && !o) continue;
    const type = name.get(typeId);
    lines.push({
      typeId,
      name: type?.name ?? typeId,
      includedInOte: type?.includedInOte ?? true,
      target,
      override: o ? o.amount : null,
      amount: o ? o.amount : target,
    });
  }
  lines.sort((a, b) => types.findIndex((t) => t.id === a.typeId) - types.findIndex((t) => t.id === b.typeId));
  const whole = latest(overrides.filter((x) => x.typeId === null), day);
  const counted = lines.filter((l) => l.includedInOte).reduce((s, l) => s + l.amount, 0);
  return {
    lines,
    targetTotal: lines.reduce((s, l) => s + l.target, 0),
    totalOverride: whole ? whole.amount : null,
    total: whole ? whole.amount : counted,
    letter,
  };
}

/**
 * BP5: what looks wrong about a person's bonus on a day. A letter or override with no pay on file
 * has no OTE to add to, and an amount on a retired type usually means a letter not yet replaced.
 */
export function bonusWarnings(bonus: BonusOn, types: readonly BonusType[], hasPay: boolean): string[] {
  const warnings: string[] = [];
  const anything = !!bonus.letter || bonus.totalOverride !== null || bonus.lines.some((l) => l.override !== null);
  if (anything && !hasPay) warnings.push("Bonus is on file but pay isn't, so there is no OTE to add it to. Add pay from the same date.");
  for (const l of bonus.lines) {
    const type = types.find((t) => t.id === l.typeId);
    if (!type || type.active) continue;
    if (l.target > 0) warnings.push(`${type.name} is retired, but the letter in effect still has a target for it.`);
    else if (l.override) warnings.push(`${type.name} is retired, but an override still sets an amount for it.`);
  }
  return warnings;
}

export function validateBonusTypes(types: readonly Partial<BonusType>[]): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  const names = new Set<string>();
  for (const t of types) {
    if (typeof t.id !== "string" || !/^[a-z0-9-]{1,40}$/.test(t.id)) errors.push("Every bonus type needs an id.");
    else if (ids.has(t.id)) errors.push("Two bonus types share an id.");
    else ids.add(t.id);
    const name = typeof t.name === "string" ? t.name.trim() : "";
    if (!name || name.length > 60) errors.push("Every bonus type needs a name of up to 60 characters.");
    else if (names.has(name.toLowerCase())) errors.push(`"${name}" is listed twice.`);
    else names.add(name.toLowerCase());
    if (typeof t.active !== "boolean" || typeof t.includedInOte !== "boolean") errors.push("Say whether each type is active and counts in OTE.");
  }
  return errors;
}

/**
 * PRD §3.4 true-up: the difference between actual and expected bonus cost (both including
 * employer taxes), spread across projects in proportion to the person's actual hours. Returns
 * a signed amount per project; the parts add up to the variance exactly.
 */
export function bonusTrueUp(expectedCost: number, actualCost: number, hoursByProject: Readonly<Record<string, number>>): Record<string, number> {
  const projects = Object.keys(hoursByProject);
  const parts = splitExact(actualCost - expectedCost, projects.map((p) => hoursByProject[p]));
  return Object.fromEntries(projects.map((p, i) => [p, parts[i]]));
}
